import { Link } from '@tanstack/react-router';
import { ArrowLeft, FileText, GraduationCap, Award, Code, Mail, Phone, Github } from 'lucide-react';
import { PageLayout } from '@/components/PageLayout';
import { useTranslation } from '@/hooks/useTypedTranslation';
import { Meta } from '@/components/MetaManager';
import { StructuredData, generateBreadcrumbs } from '@/components/StructuredData';

export default function AboutResume() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'en' | 'zh';

  const breadcrumbs = generateBreadcrumbs([
    { label: lang === 'zh' ? '关于我' : 'About', path: '/about' },
    { label: lang === 'zh' ? '简历' : 'Resume', path: '/about/resume' }
  ], lang);

  return (
    <PageLayout>
      <Meta
        title={`${t.about.resume.title} | borninsea`}
        description={t.about.resume.subtitle}
        keywords="简历,产品经理,AI,医疗科技,工作经历"
      />
      <StructuredData type="profile" breadcrumbs={breadcrumbs} />
      <div className="py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-full bg-green-500/20 border border-green-500/30">
                <FileText className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">{t.about.resume.title}</h1>
                <p className="text-muted-foreground">{t.about.resume.subtitle}</p>
              </div>
            </div>
          </div>

        {/* Contact Info */}
        <section className="mb-12 p-6 rounded-xl bg-foreground/5 border border-foreground/10">
          <div className="flex flex-wrap gap-6">
            <a href={`tel:${t.about.resume.contact.phone}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <Phone className="w-4 h-4" />
              {t.about.resume.contact.phone}
            </a>
            <a href={`mailto:${t.about.resume.contact.email}`} className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <Mail className="w-4 h-4" />
              {t.about.resume.contact.email}
            </a>
            <a href={`https://github.com/${t.about.resume.contact.github}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <Github className="w-4 h-4" />
              {t.about.resume.contact.github}
            </a>
          </div>
        </section>

        {/* Summary */}
        <section className="mb-12">
          <p className="text-lg text-foreground/80 leading-relaxed">
            {t.about.resume.summary}
          </p>
        </section>

        {/* Highlights */}
        <section className="mb-12 p-6 rounded-xl bg-green-500/10 border border-green-500/20">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-green-400" />
            {t.about.resume.highlights.title}
          </h2>
          <ul className="space-y-2 text-foreground/80">
            {t.about.resume.highlights.items.map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </section>

        {/* Skills */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <Code className="w-6 h-6" />
            {t.about.resume.skills.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {t.about.resume.skills.categories.map((skill) => (
              <div key={skill.name} className="p-4 rounded-xl bg-foreground/5 border border-foreground/10">
                <h3 className="font-bold mb-3 text-green-400">{skill.name}</h3>
                <div className="flex flex-wrap gap-2">
                  {skill.items.map((item) => (
                    <span key={item} className="text-xs px-2 py-1 rounded-full bg-foreground/10 text-foreground/70">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-8">{t.about.resume.timeline.title}</h2>
          
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[7px] md:left-[100px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500 via-green-500/50 to-green-500/20" />
            
            {/* Timeline items */}
            <div className="space-y-8">
              {t.about.resume.timeline.experiences.map((item, index) => (
                <div key={index} className="relative pl-8 md:pl-32">
                  {/* Year badge */}
                  <div className="absolute left-0 md:left-0 top-0 flex items-center">
                    <div className="w-4 h-4 rounded-full bg-green-500 border-4 border-background z-10" />
                    <span className="hidden md:block ml-4 text-sm font-mono text-green-400 w-16">
                      {item.year}
                    </span>
                  </div>
                  
                  {/* Content card */}
                  <div className="p-6 rounded-xl bg-foreground/5 border border-foreground/10 hover:border-green-500/30 transition-colors">
                    {/* Mobile year */}
                    <div className="md:hidden text-xs font-mono text-green-400 mb-2">
                      {item.year} - {item.endYear}
                    </div>
                    
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-foreground">{item.company}</h3>
                        <p className="text-green-400 font-medium">{item.role}</p>
                      </div>
                      <span className="hidden md:block text-sm text-muted-foreground whitespace-nowrap">
                        {item.year} - {item.endYear}
                      </span>
                    </div>
                    
                    <ul className="space-y-1 text-foreground/70 text-sm mb-4">
                      {item.highlights.map((highlight, idx) => (
                        <li key={idx}>• {highlight}</li>
                      ))}
                    </ul>
                    
                    {item.project && (
                      <div className="mt-4 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                        <p className="text-sm font-medium text-green-400 mb-1">
                          📦 {item.project.name}
                        </p>
                        <p className="text-xs text-foreground/60">{item.project.desc}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Education */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
            <GraduationCap className="w-6 h-6" />
            {t.about.resume.education.title}
          </h2>
          <div className="p-6 rounded-xl bg-foreground/5 border border-foreground/10">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center">
                <GraduationCap className="w-8 h-8 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold">{t.about.resume.education.school}</h3>
                <p className="text-green-400">{t.about.resume.education.degree}</p>
                <p className="text-sm text-muted-foreground">{t.about.resume.education.period}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Achievements */}
        <section className="p-6 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-green-400" />
            {t.about.resume.achievements.title}
          </h2>
          <ul className="space-y-2 text-foreground/80">
            {t.about.resume.achievements.items.map((achievement, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-green-400 mt-1">✓</span>
                {achievement}
              </li>
            ))}
          </ul>
        </section>
        </div>
      </div>
    </PageLayout>
  );
}
